import { openEventStream, type StreamStatus, type TaskStreamEvent } from '@agentic/api-client';
import type { TaskState } from '@agentic/contracts';
import { operatorApi } from '@agentic/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { caseKeys } from '../features/cases/api.ts';
import { client } from './api.ts';
import { taskStateLabel } from './presentation.ts';
import { keys, reportUnauthorized } from './queries.ts';

// ---------------------------------------------------------------------------------------------
// Screen-reader announcer (one polite live region for the whole app)
// ---------------------------------------------------------------------------------------------

const AnnounceContext = createContext<(message: string) => void>(() => undefined);

/** Renders a visually hidden `aria-live="polite"` region; `useAnnounce()` writes to it. */
export function LiveAnnouncer({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState('');
  const announce = useCallback((text: string) => {
    // Clear first so repeated identical messages are announced again.
    setMessage('');
    requestAnimationFrame(() => setMessage(text));
  }, []);
  return (
    <AnnounceContext.Provider value={announce}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        role="status"
        className="sr-only"
        data-testid="live-announcer"
      >
        {message}
      </div>
    </AnnounceContext.Provider>
  );
}

export function useAnnounce(): (message: string) => void {
  return useContext(AnnounceContext);
}

// ---------------------------------------------------------------------------------------------
// Task stream
// ---------------------------------------------------------------------------------------------

export interface LiveTaskStream {
  /** Events received since the stream opened (de-duplicated, oldest first). */
  events: TaskStreamEvent[];
  status: StreamStatus;
}

/**
 * Subscribes to `GET /tasks/{taskId}/events`. Each event invalidates the task view (and the trace, ledger,
 * audit and approvals queries it affects) so the screen converges on server state. State changes are announced
 * to screen readers once per burst, in plain language: several changes that arrive together (a reconnect replay)
 * produce one announcement of the latest state. Raw events are never announced here.
 */
export function useTaskStream(taskId: string, enabled = true): LiveTaskStream {
  const qc = useQueryClient();
  const announce = useAnnounce();
  const [events, setEvents] = useState<TaskStreamEvent[]>([]);
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const pending = useRef<Set<string>>(new Set());
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const stateChange = useRef<{ from: TaskState; to: TaskState } | null>(null);
  const announced = useRef<TaskState | null>(null);

  useEffect(() => {
    if (!enabled) return;
    setEvents([]);
    announced.current = null;
    const flush = () => {
      timer.current = undefined;
      const change = stateChange.current;
      stateChange.current = null;
      if (change && change.to !== announced.current) {
        announced.current = change.to;
        announce(`Agent status: ${taskStateLabel(change.to)} (was ${taskStateLabel(change.from)})`);
      }
      const groups = pending.current;
      pending.current = new Set();
      void qc.invalidateQueries({ queryKey: keys.task(taskId), exact: true });
      if (groups.has('trace')) void qc.invalidateQueries({ queryKey: keys.trace(taskId) });
      if (groups.has('ledger')) void qc.invalidateQueries({ queryKey: keys.ledger(taskId) });
      if (groups.has('audit')) void qc.invalidateQueries({ queryKey: keys.audit(taskId) });
      if (groups.has('approvals')) {
        void qc.invalidateQueries({ queryKey: keys.approvalsAll });
        void qc.invalidateQueries({ queryKey: ['approval'] });
      }
      if (groups.has('tasks')) void qc.invalidateQueries({ queryKey: caseKeys.all });
    };
    const handle = client.tasks.events(
      { params: { taskId } },
      {
        onEvent: (event) => {
          setEvents((prev) => [...prev, event]);
          const g = pending.current;
          g.add('trace');
          g.add('audit');
          if (event.type === 'task.state.changed') {
            g.add('tasks');
            g.add('ledger');
            stateChange.current = {
              from: stateChange.current?.from ?? event.data.from,
              to: event.data.to,
            };
          }
          if (event.type === 'action.receipt') g.add('ledger');
          if (event.type === 'approval.requested' || event.type === 'approval.decided') g.add('approvals');
          if (!timer.current) timer.current = setTimeout(flush, 120);
        },
        onStatus: setStatus,
      },
    );
    return () => {
      handle.close();
      if (timer.current) clearTimeout(timer.current);
      timer.current = undefined;
    };
  }, [taskId, enabled, qc, announce]);

  return { events, status };
}

/** Polling cadence of the case page while its live stream is off (closed after a 4xx). */
export const CASE_FALLBACK_POLL_MS = 15_000;

/** Delay before the case stream reopens after a 401 (the session was renewed or the login page took over). */
export const CASE_STREAM_REOPEN_MS = 5_000;

/** Live events of one case (`useCaseStream`): the embedded episodes' events, oldest first, plus the status. */
export interface LiveCaseStream extends LiveTaskStream {
  /** True while the stream is closed: the page polls every `CASE_FALLBACK_POLL_MS` instead. */
  off: boolean;
}

function taskIdOf(event: { subject?: string | undefined; data?: unknown }): string | undefined {
  const data = event.data as { taskId?: unknown } | undefined;
  if (typeof data?.taskId === 'string') return data.taskId;
  return event.subject;
}

/**
 * Subscribes to `GET /cases/{caseId}/events` (20a; replaces the case page's polling). Every event invalidates the
 * case's queries (detail with any selected activity, timeline, work items) and the process instances; an embedded
 * episode's events also invalidate its ledger, trace, audit and approvals. Agent state changes are announced once per
 * burst, in plain words.
 */
export function useCaseStream(caseId: string, enabled = true): LiveCaseStream {
  const qc = useQueryClient();
  const announce = useAnnounce();
  const [events, setEvents] = useState<TaskStreamEvent[]>([]);
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const tasks = useRef<Set<string>>(new Set());
  const approvals = useRef(false);
  const stateChange = useRef<TaskState | null>(null);
  // Bumped to reopen the stream after a 401 (review 6).
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    void generation;
    setEvents([]);
    let reopen: ReturnType<typeof setTimeout> | undefined;
    const flush = () => {
      timer.current = undefined;
      const to = stateChange.current;
      stateChange.current = null;
      if (to) announce(`Agent status: ${taskStateLabel(to)}`);
      void qc.invalidateQueries({ queryKey: caseKeys.detail(caseId) });
      void qc.invalidateQueries({ queryKey: ['processes', 'instances'] });
      for (const taskId of tasks.current) {
        void qc.invalidateQueries({ queryKey: keys.task(taskId) });
        void qc.invalidateQueries({ queryKey: keys.audit(taskId) });
      }
      tasks.current = new Set();
      if (approvals.current) {
        void qc.invalidateQueries({ queryKey: keys.approvalsAll });
        void qc.invalidateQueries({ queryKey: ['approval'] });
      }
      approvals.current = false;
    };
    const schedule = () => {
      if (!timer.current) timer.current = setTimeout(flush, 120);
    };
    const handle = openEventStream(
      client.url(operatorApi.cases.events, { params: { caseId } }),
      {
        onEvent: (event) => {
          setEvents((prev) => [...prev, event]);
          const taskId = taskIdOf(event);
          if (taskId) tasks.current.add(taskId);
          if (event.type === 'task.state.changed') stateChange.current = event.data.to;
          if (event.type === 'approval.requested' || event.type === 'approval.decided')
            approvals.current = true;
          schedule();
        },
        onOtherEvent: () => schedule(),
        onStatus: setStatus,
        onError: (error) => {
          // A 401 is an expired session: the app's handler (login) runs, and the stream tries again later.
          if (error.code === 'unauthorized') {
            reportUnauthorized();
            reopen = setTimeout(() => setGeneration((g) => g + 1), CASE_STREAM_REOPEN_MS);
          }
        },
      },
      client.options,
    );
    return () => {
      handle.close();
      if (reopen) clearTimeout(reopen);
      if (timer.current) clearTimeout(timer.current);
      timer.current = undefined;
    };
  }, [caseId, enabled, qc, announce, generation]);

  return { events, status, off: status === 'closed' };
}
